import { useMemo } from "react";

// A month grid with a count badge per day. Days are keyed "YYYY-MM-DD";
// `counts` maps a key to how many videos it holds. Clicking a day selects
// it (or clears the selection when it is already selected); the month
// arrows and the mode toggle report upward, so the owner can fetch data.

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const pad2 = (n) => String(n).padStart(2, "0");

export const monthKeyOf = (year, monthIndex) => `${year}-${pad2(monthIndex + 1)}`;

export function parseMonthKey(month) {
  const m = /^(\d{4})-(\d{2})$/.exec(month || "");
  if (!m) return null;
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  return monthIndex >= 0 && monthIndex < 12 ? { year, monthIndex } : null;
}

export function shiftMonth(month, delta) {
  const parsed = parseMonthKey(month);
  if (!parsed) return month;
  const date = new Date(parsed.year, parsed.monthIndex + delta, 1);
  return monthKeyOf(date.getFullYear(), date.getMonth());
}

export function monthLabel(month) {
  const parsed = parseMonthKey(month);
  return parsed ? `${MONTH_NAMES[parsed.monthIndex]} ${parsed.year}` : month || "";
}

// The 6x7 grid of a month: leading and trailing blanks are null.
export function monthGrid(month) {
  const parsed = parseMonthKey(month);
  if (!parsed) return [];
  const { year, monthIndex } = parsed;
  const first = new Date(year, monthIndex, 1).getDay();
  const days = new Date(year, monthIndex + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < first; i += 1) cells.push(null);
  for (let d = 1; d <= days; d += 1) cells.push(`${month}-${pad2(d)}`);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export const MODE_LABEL = { video: "Video date", processed: "Processed date" };

export default function MonthCalendar({
  month,
  counts = {},
  selected = null,
  onSelect,
  onMonthChange,
  mode = "video",
  onModeChange,
  today = null,
}) {
  const cells = useMemo(() => monthGrid(month), [month]);
  const total = useMemo(
    () => Object.entries(counts).reduce((sum, [key, n]) => (key.startsWith(`${month}-`) ? sum + n : sum), 0),
    [counts, month],
  );
  const todayKey = today || new Date().toISOString().slice(0, 10);

  return (
    <div className="map-calendar" data-testid="month-calendar">
      <div className="map-mode-toggle" role="radiogroup" aria-label="Calendar shows">
        {Object.entries(MODE_LABEL).map(([key, label]) => (
          <button
            type="button"
            key={key}
            role="radio"
            aria-checked={mode === key}
            className={mode === key ? "active" : ""}
            onClick={() => mode !== key && onModeChange?.(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="map-calendar-head">
        <button type="button" aria-label="Previous month" onClick={() => onMonthChange?.(shiftMonth(month, -1))}>
          &#8249;
        </button>
        <span className="map-calendar-title">{monthLabel(month)}</span>
        <button type="button" aria-label="Next month" onClick={() => onMonthChange?.(shiftMonth(month, 1))}>
          &#8250;
        </button>
      </div>

      <div className="map-calendar-grid">
        {WEEKDAYS.map((d) => (
          <span key={d} className="map-cal-weekday">{d}</span>
        ))}
        {cells.map((key, i) => {
          if (!key) return <span key={`blank-${i}`} className="map-cal-blank" />;
          const n = counts[key] || 0;
          const day = Number(key.slice(-2));
          return (
            <button
              type="button"
              key={key}
              className={`map-cal-day${n > 0 ? " has-videos" : ""}${selected === key ? " selected" : ""}${key === todayKey ? " today" : ""}`}
              disabled={n === 0}
              aria-label={`${key}: ${n} video${n === 1 ? "" : "s"}`}
              aria-pressed={selected === key}
              onClick={() => onSelect?.(selected === key ? null : key)}
            >
              <span className="map-cal-num">{day}</span>
              {n > 0 && <span className="map-cal-count">{n}</span>}
            </button>
          );
        })}
      </div>

      <div className="map-calendar-foot">
        <span>
          {selected ? `Showing ${selected}` : `Showing the whole month`} · {total} video{total === 1 ? "" : "s"} by {MODE_LABEL[mode].toLowerCase()}
        </span>
        {selected && (
          <button type="button" className="map-cal-clear" onClick={() => onSelect?.(null)}>
            Whole month
          </button>
        )}
      </div>
    </div>
  );
}
